import React, { useEffect, useState, createContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";

import { auth, db } from "./firebase/config";

import LoginPage from "./pages/LoginPage";
import CalendarPage from "./pages/CalendarPage";
import CalendarUser from "./pages/CalendarUser";
import ChangeLogsPage from "./pages/ChangeLogsPage";
import AllEventsPage from "./pages/AllEventsPage";
import UserManagement from "./pages/UserManagement";

import "./App.css";
import "./components/header.css";

/* =========================
   AUTH CONTEXT
========================= */
export const AuthContext = createContext(null);

/* =========================
   AUTH USER NORMALIZER
========================= */
const buildAuthUser = (firebaseUser) => {
  if (!firebaseUser) return null;

  const email =
    firebaseUser.email ||
    firebaseUser.providerData?.find((p) => p.email)?.email ||
    "UNKNOWN";

  return {
    uid: firebaseUser.uid,
    email,
    displayName: firebaseUser.displayName || email,
    provider: firebaseUser.providerData?.[0]?.providerId || "firebase",
  };
};

/* =========================
   PRIVATE ROUTE
========================= */
function PrivateRoute({ user, role, allowedRoles, children }) {
  if (!user) return <Navigate to="/login" replace />;

  if (allowedRoles && !role) {
    return (
      <div style={{ textAlign: "center", marginTop: 100 }}>
        Loading...
      </div>
    );
  }

  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to="/calendar" replace />;
  }

  return children;
}

/* =========================
   ROLE → HOME
========================= */
function homeByRole(role) {
  if (role === "admin") return "/admin";
  if (role === "UserAdmin") return "/usermanagement";
  return "/calendar";
}

/* =========================
   MAIN APP
========================= */
export default function App() {
  const [firebaseUser, setFirebaseUser] = useState(null); // raw auth user
  const [authUser, setAuthUser] = useState(null);         // normalized
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  /* =========================
     AUTH + ROLE LOAD
  ========================= */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log("🔥 AUTH USER UID:", currentUser?.uid || "NO USER");

      setFirebaseUser(currentUser);
      setAuthUser(buildAuthUser(currentUser));

      if (!currentUser) {
        setRole(null);
        setLoading(false);
        return;
      }

      try {
        const q = query(
          collection(db, "users"),
          where("uid", "==", currentUser.uid)
        );

        const qs = await getDocs(q);

        if (!qs.empty) {
          const userData = qs.docs[0].data();
          console.log("✅ ROLE FOUND:", userData.role);
          setRole(userData.role);
        } else {
          console.error("❌ NO USER DOC FOUND");
          setRole(null);
        }
      } catch (err) {
        console.error("🔥 ROLE FETCH ERROR:", err);
        setRole(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /* =========================
     GLOBAL LOADING
  ========================= */
  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: 100 }}>
        Loading...
      </div>
    );
  }

  /* =========================
     ROUTES
  ========================= */
  return (
    <AuthContext.Provider value={authUser}>
      <Router>
        <Routes>
          {/* LOGIN */}
          <Route
            path="/login"
            element={
              firebaseUser && role ? (
                <Navigate to={homeByRole(role)} replace />
              ) : (
                <LoginPage auth={auth} />
              )
            }
          />

          {/* ROOT */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* CALENDAR (ALL USERS) */}
          <Route
            path="/calendar"
            element={
              <PrivateRoute user={firebaseUser}>
                <CalendarUser />
              </PrivateRoute>
            }
          />

          {/* ADMIN */}
          <Route
            path="/admin"
            element={
              <PrivateRoute
                user={firebaseUser}
                role={role}
                allowedRoles={["admin"]}
              >
                <CalendarPage user={firebaseUser} />
              </PrivateRoute>
            }
          />

          {/* CHANGE LOGS */}
          <Route
            path="/changelogs"
            element={
              <PrivateRoute
                user={firebaseUser}
                role={role}
                allowedRoles={["admin"]}
              >
                <ChangeLogsPage />
              </PrivateRoute>
            }
          />

          {/* ALL EVENTS */}
          <Route
            path="/allevents"
            element={
              <PrivateRoute user={firebaseUser}>
                <AllEventsPage />
              </PrivateRoute>
            }
          />

          {/* USER MANAGEMENT */}
          <Route
            path="/usermanagement"
            element={
              <PrivateRoute
                user={firebaseUser}
                role={role}
                allowedRoles={["UserAdmin"]}
              >
                <UserManagement />
              </PrivateRoute>
            }
          />

          {/* FALLBACK */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthContext.Provider>
  );
}
